import { hash } from 'ohash';

/**
 * Groups API activity booking parts into a product info for activities
 *
 * 1. Filter out only activity booking parts
 * 2. Group them by activity, passengers and dates
 */
export function transformProductInfosActivity(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse']): ProductInfoActivity[] {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    return bookingParts
        .filter(bp => bp.objectType === 'Activity')
        .filter(bp => bp.status !== 'REFUNDED')
        .reduce<ProductInfoActivity[]>((list, bookingPart) => {
            assignActivity(list, bookingPart, apiBooking);
            return list;
        }, []);
}

/**
 * Assigns an activity booking part to the appropriate product info.
 *
 * Booking parts that belong together are grouped together. Based on:
 * - Activity details
 * - Passengers
 * - Dates
 *
 * As a result you get one activity product info per activity/passenger/date combination, which is how we want to display it in the app.
 *
 * Example: 4 People go skiing in february, 3 people go rafting im september
 * - 4 booking parts --> grouped into 1 product info with 4 passengers (skiing february)
 * - 3 booking parts --> grouped into another product info with 3 passengers (rafting september)
 */
function assignActivity(
    list: ProductInfo[],
    activity: ApiSchema['Activity'],
    apiBooking: NonNullable<ApiSchema['BookingResponse']['booking']>,
) {
    const activityId = activity.activityId ?? '';
    const type = 'Activity';
    const dateFrom = zurichDate(activity.validFrom);
    const dateUntil = zurichDate(activity.validUntil);
    const passengers = apiBooking.passengers
        .filter(p => activity.passengerIds.includes(p.id))
        .map(p => transformPassenger(p));

    // Find local product config for this activity. This is then used to:
    // - Check if there is a dynamic listingId added to the activity (dynamic product)
    // - Else, take the static listingId from the config (static product)
    // - Decide, if the listing details should be loaded from the API or from a local source
    const config = productConfigs.find(c => c.type === 'Activity' && c.activityId === activityId);
    const isLocalListing = config?.isLocalListing ?? false;
    let listingId = activity.listingId;
    if (!listingId) {
        listingId = config?.listingId ?? activityId;
    }

    // Group items by this identifier
    const id = hash([
        activityId,
        type,
        dateFrom.getTime(),
        dateUntil?.getTime(),
    ]);

    // Find or create (first) product info item
    let productInfo = list.find(p => p.id === id);
    if (!productInfo) {
        productInfo = {
            id,
            activityId,
            type,
            listingId,
            isLocalListing,
            dateFrom,
            dateUntil,
            passengers,

        } satisfies ProductInfoActivity;
        list.push(productInfo);
    }
}
